/**
 * «Accesos» en la ficha de un contacto: lo que el equipo le dio sin cobro
 * (una membresía, un paquete o un producto digital), como el «Grant offer» de
 * Kajabi. Ver `services/accessGrants.ts` en el worker.
 *
 * Dar y quitar es de quien maneja las ventas (dueño, Administrador, Finanzas);
 * el resto del equipo ve la lista pero no los botones. Sin nada dado y sin
 * permiso para dar, la tarjeta no sale.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconDownload, IconGift, IconLoader2, IconStar, IconTicket, IconX } from '@tabler/icons-react'
import {
  getContactGrants,
  grantContactAccess,
  listGrantableOffers,
  revokeContactAccess,
  type AccessGrant,
  type AccessGrantKind,
  type GrantableOffer,
} from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { useToast } from '../../Toast'
import { ConfirmDialog } from '../ConfirmDialog'
import { SingleDatePicker, todayIn } from '../DateRangePicker'
import { GrantOfferPicker } from './GrantOfferPicker'

const KIND_ICON: Record<AccessGrantKind, typeof IconStar> = { membership: IconStar, pack: IconTicket, digital: IconDownload }

export function ContactAccessCard({ projectId, identity, name, token, openSignal = 0, onCanGrant, listHidden = false }: {
  /** Sólo la ventana de «Dar acceso» (la lista va en la pestaña «Membresías»). */
  listHidden?: boolean
  projectId: string
  identity: string
  /** El nombre del contacto, para saludar en el correo. */
  name: string
  token: string
  /** Cada vez que sube, se abre «Dar acceso» (el botón vive en la cabecera de la ficha). */
  openSignal?: number
  /** Si quien mira puede dar acceso, para enseñar el botón de la cabecera. */
  onCanGrant?: (canGrant: boolean) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [data, setData] = useState<{ ready: boolean; canGrant: boolean; grants: AccessGrant[] } | null>(null)
  const [giving, setGiving] = useState(false)
  const [revoking, setRevoking] = useState<AccessGrant | null>(null)

  const load = useCallback(() => {
    if (!identity.startsWith('email:')) { setData(null); return () => undefined }
    let alive = true
    getContactGrants(projectId, identity, token)
      .then((result) => { if (alive) setData(result) })
      .catch(() => { if (alive) setData(null) })
    return () => { alive = false }
  }, [projectId, identity, token])
  useEffect(() => load(), [load])
  useEffect(() => { onCanGrant?.(Boolean(data?.canGrant)) }, [data?.canGrant, onCanGrant])
  useEffect(() => { if (openSignal > 0 && data?.canGrant) setGiving(true) }, [openSignal]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Sin nada dado, la tarjeta no ocupa sitio: «Dar acceso» está en la cabecera
     (pero la ventana sí puede abrirse desde allí). */
  if (!data || !data.ready || data.grants.length === 0 || listHidden) {
    return giving && data ? <GrantAccessDialog projectId={projectId} identity={identity} name={name} token={token} onClose={() => setGiving(false)} onDone={() => { setGiving(false); load() }} /> : null
  }

  return (
    <section className="dash-cp-card dash-cp-access">
      <div className="dash-cp-card-head">
        <h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconGift size={14} /></span> {t('contacts.access.title')}</h3>
      </div>
      {data.grants.length ? (
        <ul className="dash-cp-access-list">
          {data.grants.map((grant) => {
            const Icon = KIND_ICON[grant.kind]
            const state = grant.revokedAt ? 'revoked' : grant.active ? 'active' : 'expired'
            return (
              <li key={grant.id}>
                <div className="dash-cp-membership-head">
                  <Icon size={14} aria-hidden="true" />
                  <strong>{grant.title || t('contacts.membership.fallbackTitle')}</strong>
                  <span className={`dash-cp-membership-state is-${state === 'active' ? 'active' : 'ended'}`}>{t(`contacts.access.state.${state}`)}</span>
                </div>
                <small>
                  {[
                    t(`contacts.access.kind.${grant.kind}`),
                    grant.until ? t('contacts.access.until', { date: formatDashboardListDate(grant.until) }) : grant.kind === 'pack' ? '' : t('contacts.access.forever'),
                  ].filter(Boolean).join(' · ')}
                </small>
                <small>{t(grant.grantedByName ? 'contacts.access.givenBy' : 'contacts.access.givenOn', { name: grant.grantedByName, date: formatDashboardListDate(grant.createdAt) })}</small>
                {grant.note ? <small className="dash-cp-access-note">{grant.note}</small> : null}
                {data.canGrant && state === 'active' ? (
                  <div className="dash-cp-membership-actions">
                    <button type="button" className="dash-secondary-btn dash-cp-danger" onClick={() => setRevoking(grant)}>{t('contacts.access.revoke')}</button>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : <p className="dash-cp-access-empty">{t('contacts.access.empty')}</p>}

      {giving ? (
        <GrantAccessDialog
          projectId={projectId}
          identity={identity}
          name={name}
          token={token}
          onClose={() => setGiving(false)}
          onDone={() => { setGiving(false); load() }}
        />
      ) : null}

      {revoking ? (
        <ConfirmDialog
          tone="danger"
          title={t('contacts.access.revokeTitle')}
          message={t('contacts.access.revokeBody', { name: revoking.title })}
          confirmLabel={t('contacts.access.revoke')}
          onCancel={() => setRevoking(null)}
          onConfirm={async () => {
            try {
              await revokeContactAccess(projectId, identity, revoking.id, token)
              showToast(t('contacts.access.revoked'))
              setRevoking(null)
              load()
            } catch (error) {
              showToast(translateServerError(error, t('contacts.access.revokeFailed')), 'error')
            }
          }}
        />
      ) : null}
    </section>
  )
}

/** La ventana de «Dar acceso»: qué, hasta cuándo, nota y si se le avisa. */
function GrantAccessDialog({ projectId, identity, name, token, onClose, onDone }: {
  projectId: string
  identity: string
  name: string
  token: string
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [offers, setOffers] = useState<GrantableOffer[] | null>(null)
  const [offerId, setOfferId] = useState('')
  const [hasEnd, setHasEnd] = useState(false)
  const [until, setUntil] = useState('')
  const [note, setNote] = useState('')
  const [notify, setNotify] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    listGrantableOffers(projectId, token)
      .then((rows) => { if (alive) setOffers(rows) })
      .catch(() => { if (alive) setOffers([]) })
    return () => { alive = false }
  }, [projectId, token])

  const chosen = offers?.find((offer) => offer.offerId === offerId) ?? null
  /* El paquete caduca con su propia vigencia: no se le pone fecha aquí. */
  const canEnd = chosen !== null && chosen.kind !== 'pack'
  const today = todayIn()
  const valid = chosen !== null && (!canEnd || !hasEnd || (until !== '' && until >= today))

  const save = async () => {
    if (!valid || !chosen) return
    setSaving(true)
    setError('')
    try {
      await grantContactAccess(projectId, identity, { offerId: chosen.offerId, until: canEnd && hasEnd ? until : null, note: note.trim(), notify, name }, token)
      showToast(t(notify ? 'contacts.access.givenNotified' : 'contacts.access.given'))
      onDone()
    } catch (saveError) {
      setError(translateServerError(saveError, t('contacts.access.giveFailed')))
      setSaving(false)
    }
  }

  return createPortal(
    <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section className="dash-grant-dialog" role="dialog" aria-modal="true" aria-labelledby="grant-dialog-title">
        <header>
          <div>
            <h2 id="grant-dialog-title">{t('contacts.access.dialogTitle')}</h2>
            <p>{t('contacts.access.dialogLead')}</p>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('common.close')} disabled={saving} onClick={onClose}><IconX size={18} /></button>
        </header>

        <div className="dash-grant-body">
          <div className="dash-grant-field">
            <span>{t('contacts.access.what')}</span>
            {offers === null ? (
              <p className="dash-grant-hint"><IconLoader2 className="dash-spin" size={14} /> {t('common.loading')}</p>
            ) : offers.length === 0 ? (
              <p className="dash-grant-hint">{t('contacts.access.nothing')}</p>
            ) : (
              <GrantOfferPicker offers={offers} value={offerId} disabled={saving} onChange={setOfferId} />
            )}
          </div>

          {chosen ? (
            canEnd ? (
              <div className="dash-grant-field">
                <span>{t('contacts.access.howLong')}</span>
                <div className={`dash-svc-segmented${hasEnd ? ' is-second' : ''}`} role="radiogroup" aria-label={t('contacts.access.howLong')}>
                  <button type="button" role="radio" aria-checked={!hasEnd} className={hasEnd ? '' : 'is-on'} onClick={() => setHasEnd(false)}>{t('contacts.access.noEnd')}</button>
                  <button type="button" role="radio" aria-checked={hasEnd} className={hasEnd ? 'is-on' : ''} onClick={() => setHasEnd(true)}>{t('contacts.access.untilDate')}</button>
                </div>
                {hasEnd ? <SingleDatePicker value={until} min={today} disabled={saving} placeholder={t('contacts.access.chooseDate')} label={t('contacts.access.untilDate')} onChange={setUntil} /> : null}
              </div>
            ) : <p className="dash-grant-hint">{t('contacts.access.packHint')}</p>
          ) : null}

          <label className="dash-grant-field">
            <span>{t('contacts.access.note')}<small>{t('collections.form.optional')}</small></span>
            <textarea rows={2} maxLength={500} value={note} placeholder={t('contacts.access.notePlaceholder')} onChange={(event) => setNote(event.target.value)} />
          </label>

          <label className="dash-grant-notify">
            <input type="checkbox" checked={notify} onChange={(event) => setNotify(event.target.checked)} />
            <span><strong>{t('contacts.access.notify')}</strong><small>{t('contacts.access.notifyHint')}</small></span>
          </label>

          {error ? <p className="dash-new-booking-error" role="alert">{error}</p> : null}
        </div>

        <footer>
          <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="dash-primary-btn" disabled={saving || !valid} onClick={() => { void save() }}>
            {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconGift size={14} />}
            {t('contacts.access.giveCta')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
